/**
 * Security utilities for the application
 * Provides functions and configurations for enhancing application security
 */

import { toast } from '@/components/ui/use-toast';

/**
 * Content Security Policy configuration
 * Contains recommended CSP directives for a secure React application
 */
export const contentSecurityPolicy = {
  // Default fallback - deny by default
  'default-src': ["'self'"],

  // Scripts - only allow from self and specific CDNs
  'script-src': [
    "'self'",
    "'unsafe-inline'", // Required for React apps
    'https://www.googletagmanager.com',
    'https://www.google-analytics.com',
    'https://www.gstatic.com',
    'https://storage.googleapis.com',
    // Add nonce support for inline scripts that require it
    "'strict-dynamic'",
  ],

  // Styles - allow from self and inline (for styled-components)
  'style-src': ["'self'", "'unsafe-inline'", 'https://fonts.googleapis.com'],

  // Images - allow from self, data URIs, and specific domains
  'img-src': [
    "'self'",
    'data:',
    'https://*.supabase.co',
    'https://www.googletagmanager.com',
    'https://www.google-analytics.com',
    'https://storage.googleapis.com',
    'https:', // Allow images from any HTTPS source
    'blob:', // Allow blob URLs for image uploads preview
  ],

  // Connections - API endpoints and analytics
  'connect-src': [
    "'self'",
    'https://*.supabase.co',
    'https://www.google-analytics.com',
    'wss://*.supabase.co', // Allow WebSocket connections to Supabase Realtime
    'https://vercel.com',
    'https://vitals.vercel-insights.com',
  ],

  // Fonts - only from self
  'font-src': ["'self'", 'https://fonts.gstatic.com'],

  // Frames - only from self
  'frame-src': ["'self'"],

  // Objects - none
  'object-src': ["'none'"],

  // Form actions - only to self
  'form-action': ["'self'"],

  // Base URI - restrict to self
  'base-uri': ["'self'"],

  // Frame ancestors - deny to prevent clickjacking
  'frame-ancestors': ["'none'"],

  // Manifests - only from self
  'manifest-src': ["'self'"],

  // Media - only from self
  'media-src': ["'self'"],

  // Worker sources - only from self
  'worker-src': ["'self'", 'blob:'],

  // Add upgrade-insecure-requests directive
  'upgrade-insecure-requests': [],

  // Block HTTP form submission to HTTPS sites
  'block-all-mixed-content': [],

  // Navigation protection
  'navigate-to': ["'self'", 'https://*.elev8growth.solutions'],
};

/**
 * Generates CSP header string from configuration object
 */
export function generateCSPHeader(policy = contentSecurityPolicy): string {
  return Object.entries(policy)
    .map(([directive, sources]) => {
      // Handle directives that don't need values
      if (
        sources.length === 0 &&
        (directive === 'upgrade-insecure-requests' ||
          directive === 'block-all-mixed-content')
      ) {
        return directive;
      }
      return `${directive} ${sources.join(' ')}`;
    })
    .join('; ');
}

/**
 * Permissions Policy configuration
 * Restricts access to browser features
 */
export const permissionsPolicy = {
  camera: [],
  microphone: [],
  geolocation: [],
  'interest-cohort': [], // FLoC
  payment: [],
  usb: [],
  battery: [],
};

/**
 * Generates Permissions Policy header string from configuration object
 */
export function generatePermissionsPolicy(policy = permissionsPolicy): string {
  return Object.entries(policy)
    .map(([feature, allowList]) => {
      if (allowList.length === 0) {
        return `${feature}=()`;
      }
      return `${feature}=(${allowList.join(' ')})`;
    })
    .join(', ');
}

/**
 * CSRF protection utility
 * Generates and validates CSRF tokens using the Double Submit Cookie pattern
 */
export const csrfProtection = {
  /**
   * Generate a new CSRF token with increased entropy
   */
  generateToken(): string {
    // Generate 32 bytes of random data
    const randomBytes = new Uint8Array(32);
    window.crypto.getRandomValues(randomBytes);

    // Convert to base64 for efficiency and readability
    return btoa(String.fromCharCode.apply(null, Array.from(randomBytes)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  },

  /**
   * Store CSRF token in session storage
   */
  storeToken(token: string): void {
    sessionStorage.setItem('csrf_token', token);
  },

  /**
   * Get stored CSRF token
   */
  getToken(): string | null {
    return sessionStorage.getItem('csrf_token');
  },

  /**
   * Clear stored CSRF token
   */
  clearToken(): void {
    sessionStorage.removeItem('csrf_token');
    this.removeCookie();
  },

  /**
   * Set CSRF token as a secure HTTP-only cookie
   * This is part of the Double Submit Cookie pattern for CSRF protection
   */
  setCookie(token: string): void {
    // Create a secure, http-only, same-site cookie
    const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `X-CSRF-Token=${token}; Path=/; SameSite=Strict${secureFlag}; max-age=3600`;
  },

  /**
   * Remove CSRF cookie
   */
  removeCookie(): void {
    document.cookie =
      'X-CSRF-Token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  },

  /**
   * Generate and store a new CSRF token in both storage and cookie
   * Implements the Double Submit Cookie pattern
   */
  setupToken(): string {
    const token = this.generateToken();
    this.storeToken(token);
    this.setCookie(token);
    return token;
  },

  /**
   * Validate a CSRF token against the stored token
   * Double Submit Cookie pattern: checks both the cookie and request parameter
   */
  validateToken(token: string): boolean {
    const storedToken = this.getToken();

    // Get cookie token (server-side would check this from request cookies)
    const cookieToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('X-CSRF-Token='))
      ?.split('=')[1];

    // Token must be present in both cookie and storage, and they must match
    return (
      !!storedToken &&
      !!cookieToken &&
      storedToken === token &&
      cookieToken === token
    );
  },

  /**
   * Get CSRF token header object for use in fetch/axios requests
   */
  getTokenHeader(): Record<string, string> {
    const token = this.getToken();
    return token ? { 'X-CSRF-Token': token } : {};
  },

  /**
   * Attach CSRF token to a form
   * @param form The form element to attach the token to
   */
  attachToForm(form: HTMLFormElement): void {
    // Remove any existing CSRF input to prevent duplicates
    const existingInput = form.querySelector('input[name="csrf_token"]');
    if (existingInput) {
      existingInput.remove();
    }

    // Create and add new CSRF input
    const token = this.getToken() || this.setupToken();
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'csrf_token';
    input.value = token;
    form.appendChild(input);
  },
};

/**
 * Input sanitization functions
 */
export const sanitize = {
  /**
   * Sanitize text input to prevent XSS
   */
  text(input: string): string {
    const element = document.createElement('div');
    element.textContent = input;
    return element.innerHTML;
  },

  /**
   * Sanitize email addresses
   */
  email(input: string): string {
    return input.trim().toLowerCase();
  },

  /**
   * Sanitize URLs
   */
  url(input: string): string | null {
    try {
      const url = new URL(input);
      return url.protocol === 'http:' || url.protocol === 'https:'
        ? url.toString()
        : null;
    } catch {
      return null;
    }
  },
};

/**
 * Security event handlers
 */
export const securityHandlers = {
  /**
   * Handle security violations (CSP violations, etc.)
   */
  handleViolation(event: SecurityPolicyViolationEvent | Event): void {
    const cspEvent = event as Partial<SecurityPolicyViolationEvent>;
    const isCspViolation =
      typeof cspEvent.effectiveDirective === 'string' ||
      typeof cspEvent.violatedDirective === 'string' ||
      typeof cspEvent.blockedURI === 'string';

    // Only log in development
    if (import.meta.env.DEV) {
      if (isCspViolation) {
        console.error('Security Policy Violation:', {
          directive: cspEvent.effectiveDirective,
          blockedURI: cspEvent.blockedURI,
          violatedDirective: cspEvent.violatedDirective,
          originalPolicy: cspEvent.originalPolicy,
          disposition: cspEvent.disposition,
          documentURI: cspEvent.documentURI,
          referrer: cspEvent.referrer,
          statusCode: cspEvent.statusCode,
        });
      } else {
        console.error('Security Violation:', event);
      }
    }

    // Report-only CSP violations are telemetry. They are not blocked user actions,
    // and Vercel preview tooling can trigger them for its own fonts/scripts.
    if (isCspViolation && cspEvent.disposition === 'report') {
      return;
    }

    // Notify user only for enforced violations that the browser actually blocked.
    toast({
      title: 'Security Warning',
      description: 'A potential security issue was detected and blocked.',
      variant: 'destructive',
    });
  },

  /**
   * Set up security event listeners
   */
  setupListeners(): void {
    document.addEventListener('securitypolicyviolation', this.handleViolation);
  },

  /**
   * Remove security event listeners
   */
  removeListeners(): void {
    document.removeEventListener(
      'securitypolicyviolation',
      this.handleViolation
    );
  },
};

/**
 * Initialize all security features
 */
export function initializeSecurity(): void {
  // Always set up the CSRF token on page load. The form schemas
  // (EmailSubscriptionSchema, ContactFormSchema, LoginSchema) require a
  // non-empty csrf_token, so skipping this breaks form submissions.
  csrfProtection.setupToken();

  // Set up security event listeners
  securityHandlers.setupListeners();

  // Log security initialization in development
  if (import.meta.env.DEV) {
    console.log('Security features initialized');
  }
}
